import React from "react";

// Airplane path sourced from @swa-ui/core Graphic/graphics/airplane (viewBox 0 0 100 94).
// Inlined to avoid depending on a non-distributed internal src path.
const AIRPLANE_PATH =
  "M26.748,94h10l22.438-40H85c0,0,15,0,15-7s-15-7-15-7H59.186L36.748,0h-10l12.438,40H21.251L10,30H0l8,17L0,64h10 l11.252-10h17.935L26.748,94z";

// The neutral-900 design token resolves to white in dark mode (the app's default). Using the CSS
// custom property directly avoids the need for the non-exported getColorVariable utility.
export const NEUTRAL_900 = "var(--neutral-900)";

// T-Mobile's brand magenta. Hardcoded intentionally — it is a partner brand color with no SWA
// design token.
const T_MOBILE_MAGENTA = "#E20074";

// Reveal timing shared by the bar's scaleX and the plane's x keyframes, matching the Figma Motion
// hand-off. The plane rides the growing right edge of the bar on the shared timeline.
const REVEAL_TIMES = [0, 0.3677, 0.7753, 0.9041, 1];

// T-Mobile logo mark paths (viewBox 0 0 24 26.3, the magenta "T" logotype).
const TMOBILE_LOGO_PATHS = [
  "M8.78986 12.55H5.85986V15.48H8.78986V12.55Z",
  "M5.85986 5.81006V10.7901H6.73986V10.6401C6.73986 8.29006 8.05986 6.83006 10.5499 6.83006H10.6999V17.3701C10.6999 18.8301 10.1199 19.4201 8.64986 19.4201H8.21986V20.4501H15.8299V19.4201H15.3999C13.9399 19.4201 13.3499 18.8401 13.3499 17.3701V6.84006H13.4999C15.9799 6.84006 17.3099 8.30006 17.3099 10.6501V10.8001H18.1899V5.82006H5.85986V5.81006Z",
  "M18.14 12.55H15.21V15.48H18.14V12.55Z",
  "M19.6098 10.2399C19.6098 10.5599 19.3898 10.7899 19.0798 10.7899C18.7698 10.7899 18.5298 10.5499 18.5298 10.2399C18.5298 9.92994 18.7698 9.68994 19.0798 9.68994C19.3898 9.68994 19.6098 9.92994 19.6098 10.2399ZM19.5098 10.2399C19.5098 9.97994 19.3198 9.78994 19.0798 9.78994C18.8398 9.78994 18.6498 9.98994 18.6498 10.2399C18.6498 10.4899 18.8398 10.6899 19.0798 10.6899C19.3198 10.6899 19.5098 10.4999 19.5098 10.2399ZM19.1698 10.2999L19.2898 10.4899H19.1798L19.0698 10.2999H18.9798V10.4899H18.8798V9.97994H19.0998C19.2098 9.97994 19.2898 10.0399 19.2898 10.1399C19.2898 10.2399 19.2398 10.2799 19.1698 10.2999ZM18.9698 10.0599V10.2099H19.0898C19.1498 10.2099 19.1898 10.1999 19.1898 10.1399C19.1898 10.0799 19.1498 10.0699 19.0898 10.0699H18.9698V10.0599Z",
];

export const SCENE_CONFIG = {
  duration: 1.2,
  height: 50,
  layers: [
    {
      animate: {
        opacity: [0, 1],
        scaleX: [0, 121.64 / 361, 282.32 / 361, 1, 1],
      },
      elementProps: { fill: T_MOBILE_MAGENTA, height: 50, width: 361, x: 0, y: 0 },
      fluid: true,
      id: "background",
      initial: { opacity: 0, scaleX: 0 },
      style: { originX: 0, transformBox: "fill-box" },
      transition: {
        opacity: { ease: "linear" },
        scaleX: { ease: "linear", times: REVEAL_TIMES },
      },
      wrapper: "rect",
    },
    {
      animate: {
        opacity: [0, 1, 1],
        x: [0, 121.64, 282.32, 361, 421],
      },
      content: (
        <g transform="translate(3 0) scale(0.3) translate(-50 -47)">
          <path d={AIRPLANE_PATH} fill={NEUTRAL_900} />
        </g>
      ),
      fluid: true,
      id: "airplane",
      initial: { opacity: 0, x: 0, y: 25 },
      transition: {
        opacity: { ease: ["easeOut", "linear"], times: [0, 0.093, 1] },
        x: { ease: "linear", times: REVEAL_TIMES },
      },
      wrapper: "g",
    },
    {
      animate: {
        x: [-500, -500, 7],
      },
      content: null,
      id: "text",
      initial: { x: -500 },
      transition: {
        x: {
          ease: "linear",
          times: [0, 0.3303, 1],
        },
      },
      wrapper: "g",
    },
    {
      animate: {
        x: [-491, -491, 7],
      },
      content: (
        <g transform="translate(14 25) scale(0.9) translate(-12 -13.15)">
          <rect fill={NEUTRAL_900} height={26.3} width={24} x={0} y={0} />
          {TMOBILE_LOGO_PATHS.map((pathData) => (
            <path d={pathData} fill={T_MOBILE_MAGENTA} key={pathData} />
          ))}
        </g>
      ),
      id: "logo",
      initial: { x: -491 },
      transition: {
        x: {
          ease: "linear",
          times: [0, 0.3303, 1],
        },
      },
      wrapper: "g",
    },
  ],
  width: 361,
};
